import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Wallet, CreditCard, Banknote, FileCheck2, Smartphone, Apple, MoreHorizontal, ExternalLink, Pencil, Calendar } from "lucide-react";
import { toast } from "sonner";
import { PAYMENT_METHOD_OPTIONS, PAYMENT_METHOD_LABELS } from "@/components/RecordPaymentDialog";
import { PaymentDateEditor } from "@/components/PaymentDateEditor";

const money = (v) => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHOD_ICON = {
  credit_card: CreditCard,
  cash: Banknote,
  check: FileCheck2,
  venmo: Smartphone,
  cash_app: Smartphone,
  apple_pay: Apple,
  other: MoreHorizontal,
};

const METHOD_ACCENT = {
  credit_card: "from-indigo-500 to-indigo-600",
  cash:        "from-emerald-500 to-emerald-600",
  check:       "from-sky-500 to-sky-600",
  venmo:       "from-blue-500 to-blue-600",
  cash_app:    "from-lime-500 to-lime-600",
  apple_pay:   "from-zinc-700 to-zinc-800",
  other:       "from-amber-500 to-amber-600",
};

// Preset date ranges (local time)
function isoDate(d) { return new Date(d).toISOString().slice(0, 10); }
const RANGES = [
  { key: "30d",  label: "Last 30 days", compute: () => { const t = new Date(); const f = new Date(); f.setDate(f.getDate() - 29); return [isoDate(f), isoDate(t)]; } },
  { key: "mtd",  label: "This month",   compute: () => { const t = new Date(); const f = new Date(t.getFullYear(), t.getMonth(), 1); return [isoDate(f), isoDate(t)]; } },
  { key: "ytd",  label: "Year to date", compute: () => { const t = new Date(); const f = new Date(t.getFullYear(), 0, 1); return [isoDate(f), isoDate(t)]; } },
  { key: "all",  label: "All time",     compute: () => [null, null] },
  { key: "custom", label: "Custom",     compute: () => [null, null] },
];

export default function Payments() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialMethod = params.get("method") || "";

  const [rangeKey, setRangeKey] = useState("30d");
  const [dateFrom, setDateFrom] = useState(() => RANGES[0].compute()[0]);
  const [dateTo, setDateTo] = useState(() => RANGES[0].compute()[1]);
  const [method, setMethod] = useState(initialMethod); // filter
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  const applyRange = (key) => {
    setRangeKey(key);
    const r = RANGES.find((x) => x.key === key);
    if (!r) return;
    if (key === "custom") return; // keep existing
    const [f, t] = r.compute();
    setDateFrom(f); setDateTo(t);
  };

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo)   qs.set("date_to", dateTo);
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get(`/payments/summary?${qs.toString()}`),
        api.get(`/payments/transactions?${qs.toString()}${method ? `&method=${method}` : ""}`),
      ]);
      setSummary(s);
      setTxs(t);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load payments");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, method]);

  // keep URL ?method= in sync so a card click is bookmarkable / linkable
  useEffect(() => {
    const p = new URLSearchParams(params);
    if (method) p.set("method", method); else p.delete("method");
    setParams(p, { replace: true });
    // eslint-disable-next-line
  }, [method]);

  const updateMethod = async (tx, newMethod) => {
    try {
      await api.patch(`/invoices/${tx.invoice_id}/payments/${tx.id}/method`, { method: newMethod });
      toast.success("Payment method updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const buckets = summary?.buckets || [];
  const totalAmount = summary?.total_amount || 0;
  const totalCount = summary?.total_count || 0;

  const percent = (n) => totalAmount > 0 ? Math.round((n / totalAmount) * 100) : 0;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Billing</div>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3"><Wallet className="w-8 h-8 text-emerald-600"/> Payments</h1>
          <div className="text-sm text-zinc-500 mt-1">Break down every dollar collected by how the customer paid.</div>
        </div>
      </div>

      {/* Range selector */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4 card-crisp mb-6 flex items-center gap-3 flex-wrap">
        <Calendar className="w-4 h-4 text-zinc-400"/>
        <div className="flex gap-1 flex-wrap">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => applyRange(r.key)}
              className={`text-xs px-3 py-1.5 rounded-md border ${rangeKey === r.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"}`}
              data-testid={`payments-range-${r.key}`}
            >{r.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Input type="date" value={dateFrom || ""} onChange={(e) => { setRangeKey("custom"); setDateFrom(e.target.value || null); }} className="w-40" data-testid="payments-date-from"/>
          <span className="text-zinc-400">→</span>
          <Input type="date" value={dateTo || ""} onChange={(e) => { setRangeKey("custom"); setDateTo(e.target.value || null); }} className="w-40" data-testid="payments-date-to"/>
        </div>
      </div>

      {/* Grand total */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white rounded-xl p-6 card-crisp">
          <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-400">Total collected</div>
          <div className="font-heading text-4xl font-bold mt-1 font-mono-accent" data-testid="payments-total-amount">{money(totalAmount)}</div>
          <div className="text-xs text-zinc-400 mt-1">{totalCount} payment{totalCount === 1 ? "" : "s"} · non-voided</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp md:col-span-2">
          <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-3">Distribution</div>
          <div className="h-3 w-full rounded-full overflow-hidden bg-zinc-100 flex">
            {buckets.filter((b) => b.total > 0).map((b) => (
              <div
                key={b.method}
                className={`h-full bg-gradient-to-r ${METHOD_ACCENT[b.method] || METHOD_ACCENT.other}`}
                style={{ width: `${percent(b.total)}%` }}
                title={`${PAYMENT_METHOD_LABELS[b.method]} · ${money(b.total)}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs">
            {buckets.filter((b) => b.total > 0).map((b) => (
              <div key={b.method} className="inline-flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full bg-gradient-to-br ${METHOD_ACCENT[b.method] || METHOD_ACCENT.other}`}/>
                <span className="text-zinc-700 font-medium">{PAYMENT_METHOD_LABELS[b.method]}</span>
                <span className="text-zinc-400 font-mono-accent">{percent(b.total)}%</span>
              </div>
            ))}
            {buckets.filter((b) => b.total > 0).length === 0 && (
              <div className="text-zinc-400">No payments in this range yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Payment-type cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {PAYMENT_METHOD_OPTIONS.map((opt) => {
          const b = buckets.find((x) => x.method === opt.v) || { count: 0, total: 0 };
          const Icon = METHOD_ICON[opt.v] || MoreHorizontal;
          const active = method === opt.v;
          return (
            <button
              key={opt.v}
              onClick={() => setMethod(active ? "" : opt.v)}
              className={`text-left rounded-xl p-4 card-crisp transition-all group relative overflow-hidden border ${active ? "border-[#FF5A00] ring-2 ring-orange-100" : "border-zinc-200 hover:border-zinc-300"} bg-white`}
              data-testid={`payments-method-card-${opt.v}`}
            >
              <div className={`absolute -right-6 -top-6 w-16 h-16 rounded-full bg-gradient-to-br ${METHOD_ACCENT[opt.v]} opacity-10 group-hover:opacity-20 transition-opacity`}/>
              <div className={`w-8 h-8 rounded-md bg-gradient-to-br ${METHOD_ACCENT[opt.v]} flex items-center justify-center text-white mb-2`}>
                <Icon className="w-4 h-4"/>
              </div>
              <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{opt.l}</div>
              <div className="font-heading text-xl font-bold mt-0.5 font-mono-accent" data-testid={`payments-method-total-${opt.v}`}>{money(b.total)}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{b.count} txn{b.count === 1 ? "" : "s"}</div>
            </button>
          );
        })}
      </div>

      {/* Transactions list */}
      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">Transactions</div>
            <div className="font-heading text-lg font-bold">
              {method ? `${PAYMENT_METHOD_LABELS[method]} payments` : "All payments"}
              <span className="ml-2 text-zinc-400 font-mono-accent text-sm font-normal">{txs.length}</span>
            </div>
          </div>
          {method && (
            <Button variant="outline" size="sm" onClick={() => setMethod("")} data-testid="payments-clear-filter-btn">
              Clear filter
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-left px-4 py-3">Invoice / Customer</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Customer</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Notes / By</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="7" className="text-center text-zinc-400 py-12">Loading…</td></tr>
              )}
              {!loading && txs.map((t) => {
                const Icon = METHOD_ICON[t.method] || MoreHorizontal;
                const locked = !!t.session_id;
                const isLegacy = !locked && t.method === "other" && t.raw_method && t.raw_method !== "other";
                return (
                  <tr key={t.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`payments-row-${t.id}`}>
                    <td className="px-4 py-3">{t.invoice_id ? <PaymentDateEditor invoiceId={t.invoice_id} payment={t} disabled={!t.can_edit_date} onSaved={load}/> : <span className="font-mono-accent text-zinc-700">{t.paid_at ? new Date(t.paid_at).toLocaleDateString() : "—"}</span>}</td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium ${isLegacy ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-zinc-100 text-zinc-700"}`}>
                          <Icon className="w-3 h-3"/> {PAYMENT_METHOD_LABELS[t.method]}
                          {isLegacy && <span className="ml-1 text-[9px] uppercase font-mono-accent tracking-widest opacity-70">legacy</span>}
                        </span>
                        {!locked && (
                          <MethodEditor
                            currentMethod={t.method}
                            onChange={(m) => updateMethod(t, m)}
                            testId={`payments-edit-${t.id}`}
                          />
                        )}
                        {locked && <span className="text-[9px] uppercase font-mono-accent tracking-widest text-indigo-500" title="Stripe payments always classify as credit card">Stripe · locked</span>}
                      </div>
                      {t.check_number && (
                        <div className="text-[10px] mt-1 font-mono-accent uppercase tracking-widest text-zinc-500" data-testid={`payments-check-num-${t.id}`}>
                          Check #{t.check_number}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => nav(`/app/invoices/${t.invoice_id}`)}
                        className="text-left group inline-flex flex-col"
                        data-testid={`payments-invoice-link-${t.id}`}
                      >
                        <span className="text-[#FF5A00] group-hover:underline font-mono-accent inline-flex items-center gap-1">
                          {t.invoice_number || "—"} <ExternalLink className="w-3 h-3"/>
                        </span>
                        {t.customer_name && (
                          <span className="text-xs text-zinc-600 group-hover:text-zinc-900" data-testid={`payments-customer-${t.id}`}>
                            {t.customer_name}
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-zinc-800 hidden md:table-cell">{t.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono-accent font-semibold text-emerald-700">{money(t.amount)}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {t.notes && <div className="text-zinc-700">{t.notes}</div>}
                      {t.recorded_by_email && <div className="text-zinc-400">{t.recorded_by_email}</div>}
                    </td>
                    <td className="px-4 py-3"></td>
                  </tr>
                );
              })}
              {!loading && txs.length === 0 && (
                <tr><td colSpan="7" className="text-center text-zinc-500 py-12">
                  <Wallet className="w-8 h-8 text-zinc-300 mx-auto mb-2"/>
                  No payments {method ? `for ${PAYMENT_METHOD_LABELS[method]}` : ""} in this range.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MethodEditor({ currentMethod, onChange, testId }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
        title="Change payment method"
        data-testid={`${testId}-btn`}
      >
        <Pencil className="w-3 h-3"/>
      </button>
    );
  }
  return (
    <Select
      open
      onOpenChange={(v) => { if (!v) setOpen(false); }}
      defaultValue={currentMethod}
      onValueChange={(v) => { setOpen(false); if (v !== currentMethod) onChange(v); }}
    >
      <SelectTrigger className="h-7 w-36 text-xs" data-testid={testId}><SelectValue/></SelectTrigger>
      <SelectContent>
        {PAYMENT_METHOD_OPTIONS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
